import React, { useState, useEffect, useRef } from "react";
import { classnames } from '../../utils/classnames'

import '../partials/styles/Input.css'
import RequiredBadge from '../partials/RequiredBadge'

const CurrencyField = ({
	prefix = "$",
	centsSeparator = ".",
	thousandsSeparator = ",",

	label,
	description,
	name,
	onChange,
	isRequired,
	value,
	readOnly,
	className,
	autoComplete,
	autofocus,
	pattern,
	...rest

}) => {
	
	// Create ref for the input element
	const inputRef = useRef(null);
	
	// Internal state for the input value (allows free typing)
	const [inputValue, setInputValue] = useState("");
		
	// Track cursor position to preserve it during formatting
	const [cursorPosition, setCursorPosition] = useState(0);
	
	// Calculate new cursor position after formatting
	const calculateNewCursorPosition = (oldValue, newValue, oldCursorPos) => {
		// Count commas before cursor in old value
		const commasBeforeCursorOld = (oldValue.substring(0, oldCursorPos).match(/,/g) || []).length;
		
		// Count commas before cursor in new value
		const commasBeforeCursorNew = (newValue.substring(0, oldCursorPos).match(/,/g) || []).length;
		
		// Adjust cursor position based on comma difference
		const commaDifference = commasBeforeCursorNew - commasBeforeCursorOld;
		return Math.max(0, oldCursorPos + commaDifference);
	};
	
	// Update internal state when external value changes
	useEffect(() => {
		if (value) {
			// Remove prefix and clean the value
			let cleanValue = value.replace(prefix, "");
			
			// Remove any existing commas to normalize the value
			cleanValue = cleanValue.replace(/,/g, '');
			
			// Add commas for thousands if needed
			const parts = cleanValue.split('.');
			if (parts[0].length > 3) {
				const wholeWithCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
				cleanValue = parts.length > 1 ? wholeWithCommas + '.' + parts[1] : wholeWithCommas;
			}
			
			setInputValue(cleanValue);
		} else {
			setInputValue("");
		}
	}, [value, prefix]);

		
	// Restore cursor position after input value updates
	useEffect(() => {
		if (cursorPosition > 0 && inputRef.current) {
			inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
		}
	}, [inputValue, cursorPosition]);

	// Format the price for display (used on blur and for initial display)
	const formatPrice = (inputValue) => {
		if (!inputValue) return `${prefix}0${centsSeparator}00`;

		// Remove non-numeric characters except for digits and the cents separator
		const cleanedValue = inputValue
			.replace(new RegExp(`[^0-9${centsSeparator}]`, "g"), "")
			.replace(centsSeparator, "."); // Normalize the cents separator to a dot

		// Split into whole and fractional parts
		const [whole, fractional] = cleanedValue.split(".");
		
		// Ensure we have a whole number (default to 0 if empty)
		const wholeNumber = whole || "0";
		
		// Add thousands separator to the whole part
		const formattedWhole = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

		// Ensure we always have exactly 2 decimal places
		let formattedFractional = "00";
		if (fractional !== undefined) {
			// Pad with zeros if less than 2 digits, truncate if more than 2
			formattedFractional = fractional.substring(0, 2).padEnd(2, "0");
		}

		// Reassemble the price with the custom cents separator
		const formattedPrice = `${formattedWhole}${centsSeparator}${formattedFractional}`;

		return `${prefix}${formattedPrice}`;
	};

	// Handle input changes - allow free typing, only validate
	const handleInputChange = (e) => {
		const input = e.target;
		const cursorPos = input.selectionStart;
		const oldValue = inputValue;
		let newValue = e.target.value;
		
		// Remove any existing formatting (commas, dollar signs)
		newValue = newValue.replace(/[$,]/g, '');

		// Only allow digits and one decimal point
		newValue = newValue.replace(/[^\d.]/g, '');		
		
		// Ensure only one decimal point
		const parts = newValue.split('.');
		if (parts.length > 2) {
			newValue = parts[0] + '.' + parts.slice(1).join('');
		}
		
		// Limit decimal places to 2
		if (parts.length === 2 && parts[1].length > 2) {
			newValue = parts[0] + '.' + parts[1].substring(0, 2);
		}

		// Get the updated parts after modifications
		const updatedParts = newValue.split('.');
		
		// Add commas for thousands (only to the whole number part)
		let formattedValue = newValue;
		if (updatedParts[0].length > 3) {
			const wholeWithCommas = updatedParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
			formattedValue = updatedParts.length > 1 ? wholeWithCommas + '.' + updatedParts[1] : wholeWithCommas;
		}
		
		setInputValue(formattedValue);
		
		// Calculate new cursor position
		const newCursorPos = calculateNewCursorPosition(oldValue, formattedValue, cursorPos);
		setCursorPosition(newCursorPos);
				
		// Pass the formatted value to parent
		onChange({
			target: {
				type: 'text',
				name: name.toString(),
				value: formattedValue
			}
		});
	};

	// Handle blur - ensure proper decimal formatting when user finishes typing
	const handleBlur = () => {
		
		let finalValue = inputValue;
		if (finalValue !== '') {
			// If no decimal point, add .00
			if (!finalValue.includes('.')) {
				finalValue = finalValue + '.00';
			} else {
				// If decimal point exists, ensure exactly 2 decimal places
				const parts = finalValue.split('.');
				if (parts[1].length === 1) {
					finalValue = parts[0] + '.' + parts[1] + '0';
				} else if (parts[1].length === 0) {
					finalValue = parts[0] + '.00';
				}
			}
		}

		// Add prefix for display
		const formattedValue = finalValue !== '' ? `${prefix}${finalValue}` : '';
		setInputValue(finalValue);

		// Pass the formatted value to parent
		onChange({
			target: {
				type: 'text',
				name: name.toString(),
				value: formattedValue
			}
		});
	};

	// Display value - show what user is typing (without prefix for better UX)
	const displayValue = inputValue || (value ? value.replace(prefix, "") : "");

	if ( !readOnly ) {
		return (
			<>
				{label && (
					<label htmlFor={name}>{label} {isRequired === 1 && <RequiredBadge value={inputValue} />}{' '}</label>
				)}
				{description && (
					<div className="input--description">{description}</div>
				)}
				<input
					ref={inputRef}
					className={classnames('form-control', className)}
					autoComplete={autoComplete}
					autoFocus={autofocus}
					name={name}
					onChange={handleInputChange}
					onBlur={handleBlur}
					pattern={pattern}
					placeholder="0.00"
					type="text"
					value={displayValue}
					required={isRequired}
					{...rest}
				/>
				<div className="invalid-feedback" />
			</>
		)
	} else {
		// For read-only, show the formatted value
		const formattedDisplayValue = value ? formatPrice(value.replace(prefix, "")) : `${prefix}0${centsSeparator}00`;
		
		return (
			<>
				<label>{label} {isRequired === 1 && <RequiredBadge value={formattedDisplayValue.replace(prefix, '')} />}{' '}</label>
				<span>{formattedDisplayValue}</span>
			</>
		)
	}
};
export default CurrencyField;

/*

***** Usage *****

const App = () => {
  const [price, setPrice] = useState("");

  return (
    <div>
      <h1>Price Formatter</h1>
      <PriceInput
        value={price}
        onChange={setPrice}
        prefix="$"
        centsSeparator="."
        thousandsSeparator=","
      />
      <p>Formatted Price: {price}</p>
    </div>
  );
};
*/

